/**
 * Walmart SSO Integration Service
 * Handles OAuth 2.0 flow with Walmart through the walmart-service adapter
 */

import axios from "axios";
import { walmartAdapterUrl, walmartAuthUrl, walmartClientId } from "../constant";

// Re-export for components that need the URLs
export const WALMART_ADAPTER_URL = walmartAdapterUrl;
export const WALMART_AUTH_URL = walmartAuthUrl;
export const WALMART_CLIENT_ID = walmartClientId;

// Walmart OAuth Configuration
const WALMART_REDIRECT_URI = `${walmartAdapterUrl}/auth/walmart/callback`;
const WALMART_CLIENT_TYPE = 'seller'; // For US marketplace

/**
 * Generate a random nonce for OAuth security
 * @returns {string} Random alphanumeric string
 */
const generateNonce = () => {
    return Math.random().toString(36).substring(2, 12).toUpperCase();
};

/**
 * Generate a random state for OAuth state management
 * Encodes the seller context so the adapter can link accounts
 * @param {object} sellerContext - The seller context with companySellerId, sellerId, companyId
 * @returns {string} Base64 encoded state containing seller info and random token
 */
const generateState = (sellerContext) => {
    const randomToken = Math.random().toString(36).substring(2, 12).toUpperCase();
    // Encode seller context in state so adapter can link 3PLVision seller to Walmart account
    const stateData = {
        companySellerId: sellerContext.companySellerId,
        sellerId: sellerContext.sellerId,
        companyId: sellerContext.companyId,
        token: randomToken,
        timestamp: Date.now()
    };
    return btoa(JSON.stringify(stateData));
};

/**
 * Check Walmart connection status for a seller
 * @param {string|number} sellerId - The seller ID to check status for
 * @returns {Promise<{connected: boolean, connectedAt?: string}>}
 */
export const checkWalmartConnectionStatus = async (sellerId) => {
    try {
        const response = await axios.get(`${WALMART_ADAPTER_URL}/auth/walmart/status/${sellerId}`);
        return {
            connected: response?.data?.data?.connected || false,
            connectedAt: response?.data?.data?.connectedAt || null,
        };
    } catch (error) {
        console.error('Error checking Walmart connection status:', error);
        return { connected: false, connectedAt: null };
    }
};

/**
 * Get the current seller ID from storage
 * Checks currentCompany (selected context), userDetails, and user objects
 * @returns {{companySellerId: string|null, sellerId: string|null, companyId: string|null}}
 */
const getCurrentSellerContext = () => {
    // Priority 1: Check currentCompany (user's selected company/seller context)
    const currentCompany = JSON.parse(localStorage.getItem('currentCompany') || '{}');
    if (currentCompany?.companySellerId) {
        return {
            companySellerId: currentCompany.companySellerId,
            sellerId: currentCompany.sellerId,
            companyId: currentCompany.companyId,
            companySellerName: currentCompany.companySellerName
        };
    }
    
    // Priority 2: Check userDetails (login response)
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    if (userDetails?.Value?.companySellerId) {
        return {
            companySellerId: userDetails.Value.companySellerId,
            sellerId: userDetails.Value.sellerId,
            companyId: userDetails.Value.companyId,
            companySellerName: null
        };
    }
    
    // Priority 3: Check user object
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user?.Value?.companySellerId) {
        return {
            companySellerId: user.Value.companySellerId,
            sellerId: user.Value.sellerId,
            companyId: user.Value.companyId,
            companySellerName: null
        };
    }
    
    return { companySellerId: null, sellerId: null, companyId: null, companySellerName: null };
};

/**
 * Check if user has a valid seller context
 * @returns {boolean}
 */
export const hasSellerContext = () => {
    const context = getCurrentSellerContext();
    return !!context.companySellerId;
};

/**
 * Initiate Walmart OAuth flow by redirecting directly to Walmart login
 * @param {object} sellerContextOverride - Optional seller context to use instead of auto-detecting
 *        Should have: { companySellerId, sellerId, companyId }
 */
export const initiateWalmartOAuth = (sellerContextOverride = null) => {
    let sellerContext;
    
    if (sellerContextOverride && sellerContextOverride.companySellerId) {
        // Use provided context
        sellerContext = {
            companySellerId: sellerContextOverride.companySellerId,
            sellerId: sellerContextOverride.sellerId,
            companyId: sellerContextOverride.companyId
        };
    } else {
        // Auto-detect from storage
        sellerContext = getCurrentSellerContext();
    }
    
    if (!sellerContext.companySellerId) {
        console.error('Cannot initiate Walmart OAuth: No seller context available');
        throw new Error('Please select a Company/Seller before connecting to Walmart');
    }
    
    const nonce = generateNonce();
    const state = generateState(sellerContext);
    
    // Store state in sessionStorage for validation on callback
    sessionStorage.setItem('walmart_oauth_state', state);
    sessionStorage.setItem('walmart_oauth_company_seller_id', sellerContext.companySellerId);
    
    // Construct URL matching Walmart docs example format
    const authUrl = `${WALMART_AUTH_URL}?responseType=code&clientId=${WALMART_CLIENT_ID}&redirectUri=${WALMART_REDIRECT_URI}&nonce=${nonce}&state=${state}&clientType=${WALMART_CLIENT_TYPE}`;
    
    console.log('Redirecting to Walmart OAuth for companySellerId:', sellerContext.companySellerId);
    window.location.href = authUrl;
};

/**
 * Initiate Walmart OAuth flow with custom callback URI and client type
 * Used for Walmart-initiated flows where Walmart provides the callback URL
 * @param {string} walmartCallbackUri - The callback URI provided by Walmart
 * @param {string} clientType - The client type (seller, vendor, etc.)
 */
export const initiateWalmartOAuthWithParams = (walmartCallbackUri, clientType) => {
    const sellerContext = getCurrentSellerContext();
    
    if (!sellerContext.companySellerId) {
        console.error('Cannot initiate Walmart OAuth: No seller context available');
        throw new Error('Please select a Company/Seller before connecting to Walmart');
    }
    
    const nonce = generateNonce();
    const state = generateState(sellerContext);
    
    // Store state and Walmart callback info in sessionStorage
    sessionStorage.setItem('walmart_oauth_state', state);
    sessionStorage.setItem('walmart_callback_uri', walmartCallbackUri);
    sessionStorage.setItem('walmart_client_type', clientType);
    sessionStorage.setItem('walmart_oauth_company_seller_id', sellerContext.companySellerId);
    
    // Use Walmart's provided callback URI as the redirect
    const authUrl = `${WALMART_AUTH_URL}?responseType=code&clientId=${WALMART_CLIENT_ID}&redirectUri=${walmartCallbackUri}&nonce=${nonce}&state=${state}&clientType=${clientType}`;
    
    console.log('Redirecting to Walmart OAuth (Walmart-initiated) for companySellerId:', sellerContext.companySellerId);
    window.location.href = authUrl;
};

/**
 * Disconnect Walmart account for a seller
 * @param {string|number} sellerId - The seller ID to disconnect
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const disconnectWalmartAccount = async (sellerId) => {
    try {
        const response = await axios.delete(`${WALMART_ADAPTER_URL}/auth/walmart/disconnect/${sellerId}`);
        return {
            success: true,
            message: response?.data?.message || 'Disconnected successfully',
        };
    } catch (error) {
        console.error('Error disconnecting Walmart account:', error);
        return { 
            success: false, 
            message: error?.response?.data?.message || 'Failed to disconnect Walmart account' 
        };
    }
};
